/**
 * SettingsPanel — fee-calculator configuration UI. Mounted inside
 * Settings → Fee Calculator (adviser-only); not exposed in the Tools
 * page itself. Wires its draft state to the supplied onSave callback,
 * which the parent connects to Supabase via useFeeCalculatorSettings().
 *
 * Ported verbatim from optimum-fees-calculator.jsx (SettingsPanel
 * component) so the existing UX (hourly-rate helper, save/reset
 * buttons, defaults reminder) is preserved exactly.
 */

import React, { useState, useEffect } from "react";
import { C } from "./brand";
import { DEFAULTS, fmtZAR, fmtPct } from "./feeEngine";
import { Field, NumberInput } from "./primitives";

export default function SettingsPanel({ settings, onSave }) {
  const [draft, setDraft] = useState(settings);
  const [saved, setSaved] = useState(false);

  // Hourly rate calculator — local state only (not persisted).
  const [helperOpen, setHelperOpen] = useState(false);
  const [helperGross, setHelperGross] = useState(null);
  const [helperHours, setHelperHours] = useState(2080);

  useEffect(() => setDraft(settings), [settings]);

  const update = (k, v) => {
    setDraft((d) => ({ ...d, [k]: v }));
    setSaved(false);
  };

  const save = async () => {
    await onSave(draft);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const reset = () => {
    setDraft(DEFAULTS);
    setSaved(false);
  };

  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);

  // Suggested rate from the helper (only when both inputs are valid).
  const suggestedRate =
    helperGross && helperGross > 0 && helperHours && helperHours > 0
      ? Math.round(helperGross / helperHours)
      : null;

  const applySuggested = () => {
    if (suggestedRate !== null) update("hourlyRate", suggestedRate);
  };

  return (
    <div style={{ maxWidth: 560 }}>
      <h2 style={{ color: C.tealDark, fontSize: 22, fontWeight: 600, marginBottom: 4 }}>Fee Calculator settings</h2>
      <p style={{ color: C.muted, fontSize: 13, marginBottom: 24 }}>
        Editable values. Changes persist across sessions and are shared firm-wide.
      </p>

      {/* Hourly rate calculator helper */}
      <div
        style={{
          marginBottom: 20,
          border: `1px solid ${C.border}`,
          borderRadius: 4,
          background: C.white,
          overflow: "hidden",
        }}
      >
        <button
          onClick={() => setHelperOpen((o) => !o)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 14px",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: C.teal,
            fontSize: 12,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
          aria-expanded={helperOpen}
        >
          <span>Calculate hourly rate from annual gross</span>
          <span style={{ fontSize: 14, transition: "transform 0.2s", transform: helperOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▾</span>
        </button>

        {helperOpen && (
          <div style={{ padding: "4px 14px 14px", borderTop: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.6 }}>
              Divides previous tax year gross income by billable hours per year to suggest an hourly rate.
              Default 2,080 hours = 8 × 5 × 52 (8 hours/day × 5 days/week × 52 weeks/year — standard SA full-time hours).
            </div>

            <Field label="Previous tax year gross income">
              <NumberInput
                value={helperGross}
                onChange={setHelperGross}
                prefix="R"
                placeholder="0"
                format="currency"
              />
            </Field>

            <Field label="Billable hours per year" hint="Edit if your assumption differs from the default.">
              <NumberInput
                value={helperHours}
                onChange={(v) => setHelperHours(v ?? 0)}
                suffix="hrs"
                min={1}
                step={10}
              />
            </Field>

            {suggestedRate !== null && (
              <div
                style={{
                  marginTop: 8,
                  padding: 14,
                  background: "#F2F7F8",
                  borderLeft: `3px solid ${C.tealDark}`,
                  borderRadius: 4,
                }}
              >
                <div style={{ fontSize: 11, color: C.teal, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 4 }}>
                  Suggested hourly rate
                </div>
                <div style={{ fontSize: 24, fontWeight: 600, color: C.tealDark, fontVariantNumeric: "tabular-nums" }}>
                  {fmtZAR(suggestedRate)}
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4, fontFamily: "monospace" }}>
                  {fmtZAR(helperGross)} ÷ {helperHours.toLocaleString("en-ZA")} hrs = {fmtZAR(suggestedRate)}/hr
                </div>
                <button
                  onClick={applySuggested}
                  style={{
                    marginTop: 12,
                    background: C.tealDark,
                    color: C.white,
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: 4,
                    fontWeight: 600,
                    fontSize: 12,
                    cursor: "pointer",
                    letterSpacing: 0.3,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = C.teal)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = C.tealDark)}
                >
                  Apply this rate ↓
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <Field label="Hourly rate (ZAR)" hint="Used as the basis for the initial fee back-solve. Use the helper above to calculate from annual gross income.">
        <NumberInput value={draft.hourlyRate} onChange={(v) => update("hourlyRate", v ?? 0)} prefix="R" format="currency" />
      </Field>

      <Field label="Minimum lump-sum threshold (ZAR)" hint="No initial fee charged on lump sums below this amount.">
        <NumberInput value={draft.minThreshold} onChange={(v) => update("minThreshold", v ?? 0)} prefix="R" format="currency" />
      </Field>

      <Field label="Initial fee cap (% of investment, excl. VAT)" hint="The maximum gross initial fee, expressed as a percentage of the investment amount.">
        <NumberInput value={draft.feeCap * 100} onChange={(v) => update("feeCap", (v ?? 0) / 100)} suffix="%" min={0} step={0.05} />
      </Field>

      <Field label="VAT rate (%)" hint="Applied on top of all gross fees.">
        <NumberInput value={draft.vatRate * 100} onChange={(v) => update("vatRate", (v ?? 0) / 100)} suffix="%" min={0} step={0.5} />
      </Field>

      <div className="flex items-center gap-3 mt-6">
        <button
          onClick={save}
          disabled={!dirty}
          style={{
            background: dirty ? C.tealDark : C.border,
            color: dirty ? C.white : C.muted,
            border: "none",
            padding: "10px 20px",
            borderRadius: 4,
            fontWeight: 600,
            fontSize: 13,
            cursor: dirty ? "pointer" : "not-allowed",
            letterSpacing: 0.3,
          }}
        >
          Save changes
        </button>
        <button
          onClick={reset}
          style={{
            background: "transparent",
            color: C.teal,
            border: `1px solid ${C.border}`,
            padding: "10px 20px",
            borderRadius: 4,
            fontWeight: 500,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Reset to defaults
        </button>
        {saved && <span style={{ fontSize: 13, color: C.tealMid, fontWeight: 500 }}>✓ Saved</span>}
      </div>

      <div
        style={{
          marginTop: 32,
          padding: 14,
          background: C.bgSoft,
          borderLeft: `3px solid ${C.orange}`,
          fontSize: 12,
          color: C.muted,
          lineHeight: 1.6,
        }}
      >
        Defaults: Hourly rate {fmtZAR(DEFAULTS.hourlyRate)} • Threshold{" "}
        {fmtZAR(DEFAULTS.minThreshold)} • Fee cap {fmtPct(DEFAULTS.feeCap, 0)} • VAT{" "}
        {fmtPct(DEFAULTS.vatRate, 0)}.
      </div>
    </div>
  );
}
